// /forgot-password — user enters email, server sends reset link.
// Always confirms "If an account exists, an email has been sent" to avoid user enumeration.

import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Loader2, Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import Logo from "../components/Logo";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Enter your email"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      setSent(true);
    } catch {
      // Backend always returns 200 — if we end up here, network error
      toast.error("Couldn't reach the server. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col">
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" data-testid="forgot-home-link"><Logo size={28} /></Link>
          <Link to="/login" className="text-sm text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1" data-testid="forgot-back-to-login">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="bg-white border border-zinc-200 rounded p-8 shadow-sm" data-testid="forgot-password-card">
            {sent ? (
              <div className="text-center" data-testid="forgot-success-state">
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                <h1 className="text-xl font-bold text-zinc-900 mb-2">Check your email</h1>
                <p className="text-sm text-zinc-500 mb-4">
                  If an account exists for <strong className="text-zinc-700">{email}</strong>, we've sent a password reset link. It expires in 1 hour.
                </p>
                <p className="text-xs text-zinc-400">
                  Don't see it? Check your spam folder. Still nothing? Contact your administrator — they can find the reset link in admin tools.
                </p>
                <Link to="/login" className="inline-block mt-6 text-sm text-[#002FA7] hover:underline font-medium" data-testid="forgot-success-back-link">Back to sign in</Link>
              </div>
            ) : (
              <>
                <h1 className="text-2xl font-bold tracking-tight text-zinc-900 mb-1">Forgot your password?</h1>
                <p className="text-sm text-zinc-500 mb-6">Enter your email and we'll send you a reset link.</p>
                <form onSubmit={handleSubmit} className="space-y-3">
                  <div>
                    <label className="text-xs uppercase tracking-wide text-zinc-500 flex items-center gap-1"><Mail className="w-3 h-3" /> Email</label>
                    <input
                      type="email"
                      required
                      autoFocus
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full mt-1 px-3 py-2 border border-zinc-200 rounded text-sm bg-white"
                      data-testid="forgot-email-input"
                      autoComplete="email"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-2.5 px-4 bg-[#002FA7] hover:bg-[#00227A] text-white text-sm font-medium rounded inline-flex items-center justify-center gap-2 disabled:opacity-60"
                    data-testid="forgot-submit-btn"
                  >
                    {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : "Send reset link"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ForgotPassword;
